import { useEffect, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Select } from '@/components/ui/select';

import type { TenantTemplateViewModel } from '../api';

export function ApplyTemplateDialog({
  open,
  onOpenChange,
  templates,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly templates: readonly TenantTemplateViewModel[];
  readonly onSubmit: (templateName: string) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    if (open && templateName === '' && templates.length > 0) {
      setTemplateName(templates[0]?.name ?? '');
    }
  }, [open, templates, templateName]);

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    if (templateName.length === 0) return;
    onSubmit(templateName);
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTemplateName('');
        onOpenChange(next);
      }}
    >
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.detail.applyTemplate.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.detail.applyTemplate.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField id="apply-template-name" label={t('routes.platform.detail.applyTemplate.field')}>
            {(fp) => (
              <Select
                {...fp}
                value={templateName}
                onChange={(e) => { setTemplateName(e.target.value); }}
              >
                {templates.map((tpl) => (
                  <option key={tpl.name} value={tpl.name}>
                    {tpl.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.detail.applyTemplate.error', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onOpenChange(false); }}
              disabled={pending}
            >
              {t('routes.platform.detail.applyTemplate.cancel')}
            </Button>
            <Button type="submit" loading={pending} disabled={templateName.length === 0}>
              {t('routes.platform.detail.applyTemplate.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
